<template>
	<nav
		ref="menu"
		role="menu"
		class="menu"
		outline="none"
	>
		<slot/>
	</nav>
</template>

<script>
export default { name: 'TMenu', };
</script>

<style lang="scss" scoped>
	.menu {
		box-sizing: border-box;
	}
</style>

<docs>
## Menu

### Default
```jsx noeditor
var VUE = require('vue').default;
let TMenuGroup = require('./TMenuGroup.vue').default;
VUE.component('TMenuGroup', TMenuGroup);
let TMenuItem = require('./TMenuItem.vue').default;
VUE.component('TMenuItem', TMenuItem);
let TMenuDivider = require('./TMenuDivider.vue').default;
VUE.component('TMenuDivider', TMenuDivider);
let TMenuOptionsGroup = require('./TMenuOptionsGroup.vue').default;
VUE.component('TMenuOptionsGroup', TMenuOptionsGroup);
let TMenuOptionItem = require('./TMenuOptionItem.vue').default;
VUE.component('TMenuOptionItem', TMenuOptionItem);
let TMenuFilterOptionItem = require('./TMenuFilterOptionItem.vue').default;
VUE.component('TMenuFilterOptionItem', TMenuFilterOptionItem);
let TMenuComboboxOptionItem = require('./TMenuComboboxOptionItem.vue').default;
VUE.component('TMenuComboboxOptionItem', TMenuComboboxOptionItem);

<t-menu>
	<t-menu-group title="actions">
		<t-menu-item icon="add" value="New...">
			<div slot="after">Hello?</div>
		</t-menu-item>
		<t-menu-item icon="edit" secondaryText="⌘R">Rename...</t-menu-item>
		<t-menu-item icon="lock">Lock...</t-menu-item>
	</t-menu-group>
	<t-menu-divider />
	<t-menu-group title="destructive">
		<t-menu-item icon="trash" intent="danger">Delete...</t-menu-item>
	</t-menu-group>
</t-menu>
```
```html
<t-menu>
	<t-menu-group title="actions">
		<t-menu-item icon="add">New...</t-menu-item>
		<t-menu-item icon="edit" secondaryText="⌘R">Rename...</t-menu-item>
		<t-menu-item icon="lock">Lock...</t-menu-item>
	</t-menu-group>
	<t-menu-divider />
	<t-menu-group title="destructive">
		<t-menu-item icon="trash" intent="danger">Delete...</t-menu-item>
	</t-menu-group>
</t-menu>
```

### Options Group
```jsx noeditor
new Vue({
	data(){
		return {
			tripOptions: ['Tursøk','Lokale turer', 'Faste turer', 'Turer uten oppdrag'],
			orderOptions: ['Oddragssøk','Nye inkomne','Mine oppdrag', 'Opprag med avvik', 'Ufulstendig registrerte'],
		}
	},
	template: `
		<t-menu>
			<t-menu-options-group title="Turer" :selectedItem="tripOptions[0]" :items="tripOptions" v-slot="slotProps">
				<t-menu-option-item :value="slotProps.item" :selected="slotProps.isSelected" />
			</t-menu-options-group>
			<t-menu-divider/>
			<t-menu-options-group title="Oppdrag" :selectedItem="orderOptions[0]" :items="orderOptions" v-slot="slotProps">
				<t-menu-option-item :value="slotProps.item" :selected="slotProps.isSelected" />
			</t-menu-options-group>
		</t-menu>
	`
});
```
```html
...js
orderOptions: ['Ascending','Descending'],
showOptions: ['Email','Phone','State'],
...

<t-menu>
	<t-menu-options-group title="Order" :selectedItem="orderOptions[0]" :options="orderOptions" v-slot="slotProps">
		<t-menu-option-item :value="slotProps.item" :selected="slotProps.isSelected" />
	</t-menu-options-group>
	<t-menu-divider/>
	<t-menu-options-group title="show" :selectedItem="showOptions[0]" :options="showOptions" v-slot="slotProps">
		<t-menu-option-item :value="slotProps.item" :selected="slotProps.isSelected" />
	</t-menu-options-group>
</t-menu>
```

#### OptionItem Types

##### Filter

```jsx noeditor
new Vue({
	data(){
		return {
			tripItems: [
				{description: 'Tursøk', icon: 'code-branch', bold: true},
				{description: 'Lokale turer', count: 3, isNew: false},
				{description: 'Faste turer', count: 0, isNew: false},
				{description: 'Turer uten oppdrag', count: 10, isNew: true},
				{description: 'Ny tur', icon: 'plus', bold: true}],
			orderItems: [
				{description: 'Oppdragssøk', icon: 'cube', bold: true},
				{description: 'Nye inkomne', count: 3, isNew: true},
				{description: 'Mine oppdrag', count: 5, isNew: false},
				{description: 'Oppdrag med avvik', count: 1, isNew: true},
				{description: 'Nytt oppdrag', icon: 'plus', bold: true}],
			lastetorg: [
				{description: 'Kartvisning', icon: 'globe', bold: true},
				{description: 'Listevisning', icon: 'list-alt', bold: true},
				{description: 'Eksterne', icon: 'project-diagram', bold: true}],
			}
	},
	template: `

		<t-menu style="width: 220px; background-color: #e4e7eb; padding: 10px 0;">
			<t-menu-options-group title="Turer" :items="tripItems" v-slot="slotProps" bold>
				<t-menu-filter-option-item :value="slotProps.item" :selected="slotProps.isSelected" :count="slotProps.item.count"></t-menu-filter-option-item>
			</t-menu-options-group>

			<t-menu-divider :border="false" large/>

			<t-menu-options-group title="Oppdrag" :items="orderItems" v-slot="slotProps" bold>
				<t-menu-filter-option-item :value="slotProps.item" :selected="slotProps.isSelected" :count="slotProps.item.count"></t-menu-filter-option-item>
			</t-menu-options-group>

			<t-menu-divider :border="false" large/>

			<t-menu-options-group title="Lastetorg" :items="lastetorg" v-slot="slotProps" bold>
				<t-menu-filter-option-item :value="slotProps.item" :selected="slotProps.isSelected" :count="slotProps.item.count"></t-menu-filter-option-item>
			</t-menu-options-group>
		</t-menu>

	`
});
```

```html
...js
tripItems: [
	{description: 'Tursøk', icon: 'code-branch', bold: true},
	{description: 'Lokale turer', count: 3, isNew: false},
	{description: 'Faste turer', count: 0, isNew: false},
	{description: 'Turer uten oppdrag', count: 10, isNew: true},
	{description: 'Ny tur', icon: 'plus', bold: true}],
orderItems: [
	{description: 'Oppdragssøk', icon: 'cube', bold: true},
	{description: 'Nye inkomne', count: 3, isNew: true},
	{description: 'Mine oppdrag', count: 5, isNew: false},
	{description: 'Oppdrag med avvik', count: 1, isNew: true},
	{description: 'Nytt oppdrag', icon: 'plus', bold: true}],
...
<t-menu style="width: 220px; background-color: #e4e7eb; padding: 10px 0;">
	<t-menu-options-group title="Turer" :items="tripItems" v-slot="slotProps" bold>
		<t-menu-filter-option-item :value="slotProps.item" :selected="slotProps.isSelected" :count="slotProps.item.count"></t-menu-filter-option-item>
	</t-menu-options-group>

	<t-menu-divider :border="false" large/>

	<t-menu-options-group title="Oppdrag" :items="orderItems" v-slot="slotProps" bold>
		<t-menu-filter-option-item :value="slotProps.item" :selected="slotProps.isSelected" :count="slotProps.item.count"></t-menu-filter-option-item>
	</t-menu-options-group>

	<t-menu-divider :border="false" large/>

	<t-menu-group title="lastetorg">
		<t-menu-filter-option-item :value="slotProps.item" :selected="slotProps.isSelected" :count="slotProps.item.count"></t-menu-filter-option-item>
	</t-menu-group>
</t-menu>
```

##### Combobox

```jsx noeditor
new Vue({
	data(){
		return {
			items: ['Apple', 'Orange', 'Kiwi'],
		}
	},
	template: `
		<t-menu>
			<t-menu-options-group title="Fruits" :selectedItem="items[0]" :items="items" v-slot="slotProps" groupStyle="combobox">
				<t-menu-combobox-option-item :value="slotProps.item" :selected="slotProps.isSelected"></t-menu-combobox-option-item>
			</t-menu-options-group>
		</t-menu>
	`
});
```

```html
...js
items: ['Apple', 'Orange', 'Kiwi'],
...
<t-menu>
	<t-menu-options-group title="Fruits" :selectedItem="items[0]" :items="items" v-slot="slotProps" groupStyle="combobox">
		<t-menu-combobox-option-item :value="slotProps.item" :selected="slotProps.isSelected"></t-menu-combobox-option-item>
	</t-menu-options-group>
</t-menu>
```

### Slots

#### Item-slot
In cases where your items are an array of objects instead of strings
```jsx noeditor
new Vue({
	data(){
		return {
			items: [{label: 'Foo'}, {label: 'Bar'}, {label: 'Baz'}],
		}
	},
	template: `
		<t-menu>
			<t-menu-options-group title="Foo/Bar/baz" :selectedItem="items[0]" :items="items" v-slot="slotProps">
				<t-menu-option-item :value="slotProps.item" :selected="slotProps.isSelected">
					{{slotProps.item.label}}
				</t-menu-option-item>
			</t-menu-options-group>
		</t-menu>
	`
});
```

```html
...js
items: [{label: 'Foo'}, {label: 'Bar'}, {label: 'Baz'}]
...
<t-menu>
	<t-menu-options-group title="Foo/Bar/baz" :selectedItem="items[0]" :items="items" v-slot="slotProps">
		<t-menu-option-item :value="slotProps.item" :selected="slotProps.isSelected">
			{{slotProps.item.label}}
		</t-menu-option-item>
	</t-menu-options-group>
</t-menu>
```

</docs>
